import React, { useState } from "react";
import { Menu } from "@headlessui/react";
import { Download, X, RefreshCw, RotateCcw, Plus, Calendar, MoreVertical, Unlink } from "lucide-react";
import { Button } from "./Button";
import { ShimmerText } from "./ShimmerText";
import { Alert } from "./Alert";
import { DateRangeConfig, isDateColumn } from "./DateRangeConfig";
import { fetchSourceMetadata } from "../services/api";
import type { ImportCardData, ColumnMetadata } from "../types/imports";

/**
 * Map a date preset ID to a human-readable label.
 */
function formatDatePresetLabel(preset: string, afterMonth?: string): string {
  const labels: Record<string, string> = {
    "this-year": "This Year",
    "this-quarter": "This Quarter",
    "this-month": "This Month",
    "last-year": "Last Year",
    "last-quarter": "Last Quarter",
    "last-month": "Last Month",
  };
  if (preset === "after" && afterMonth) {
    const parts = afterMonth.split("-").map(Number);
    const year = parts[0];
    const month = parts[1];
    const day = parts[2] || 1;
    const date = new Date(year, month - 1, day);
    if (!isNaN(date.getTime())) {
      return `≥ ${date.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" })}`;
    }
    return `≥ ${afterMonth}`;
  }
  return labels[preset] || preset;
}

/**
 * Format an ISO timestamp into a human-friendly label prefixed with "Refreshed".
 */
function formatTimestamp(value: string): string {
  const date = new Date(value);
  if (isNaN(date.getTime())) return value;

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  const diffHours = Math.floor(diffMs / 3_600_000);

  if (diffMin < 1) return "Refreshed less than a minute ago";
  if (diffMin < 60) return `Refreshed ${diffMin} minute${diffMin === 1 ? "" : "s"} ago`;
  if (diffHours < 2) return `Refreshed ${diffHours} hour ago`;

  const timeStr = date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

  // Same calendar day
  if (
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()
  ) {
    return `Refreshed Today at ${timeStr}`;
  }

  // Yesterday
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (
    date.getFullYear() === yesterday.getFullYear() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getDate() === yesterday.getDate()
  ) {
    return `Refreshed Yesterday at ${timeStr}`;
  }

  // Older
  const monthDay = date.toLocaleDateString([], { month: "short", day: "numeric" });
  return `Refreshed ${monthDay} at ${timeStr}`;
}

interface ImportCardProps {
  card: ImportCardData;
  orgId?: string | null;
  onImport?: (id: string) => void;
  onRefresh?: (id: string) => void;
  onRemove?: (id: string) => void;
  onRetry?: (id: string) => void;
  onSheetClick?: (sheetId: string) => void;
  onProceedWithWarning?: (id: string) => void;
  onCancelWarning?: (id: string) => void;
  onDateFilterChange?: (
    id: string,
    dateColumn: string | undefined,
    dateColumnDisplayName: string | undefined,
    datePreset: string | undefined,
    afterMonth: string | undefined,
  ) => void;
  onReimport?: (id: string) => void;
  onDateFilterChangeAndRefresh?: (
    id: string,
    dateColumn: string | undefined,
    dateColumnDisplayName: string | undefined,
    datePreset: string | undefined,
    afterMonth: string | undefined,
  ) => void;
}

export function ImportCard({
  card,
  orgId,
  onImport,
  onRefresh,
  onRemove,
  onRetry,
  onSheetClick,
  onProceedWithWarning,
  onCancelWarning,
  onDateFilterChange,
  onReimport,
  onDateFilterChangeAndRefresh,
}: ImportCardProps) {
  const [showConfirm, setShowConfirm] = useState(false);
  const [showDateFilter, setShowDateFilter] = useState(
    !!(card.dateColumn && card.datePreset),
  );
  const [dateColumns, setDateColumns] = useState<ColumnMetadata[]>([]);
  const [loadingMetadata, setLoadingMetadata] = useState(false);
  const [noDateColumns, setNoDateColumns] = useState(false);

  // Edit filter state for imported cards
  const [isEditingFilter, setIsEditingFilter] = useState(false);
  const [editDateColumn, setEditDateColumn] = useState<string | undefined>(card.dateColumn);
  const [editDatePreset, setEditDatePreset] = useState<string | undefined>(card.datePreset);
  const [editAfterMonth, setEditAfterMonth] = useState<string | undefined>(card.afterMonth);

  const dateFilterLabel =
    card.dateColumn && card.datePreset
      ? `${card.dateColumnDisplayName || card.dateColumn} ${card.datePreset === "after" ? "" : "= "}${formatDatePresetLabel(card.datePreset, card.afterMonth)}`
      : "Add date filter";

  const handleOpenDateFilter = async () => {
    if (dateColumns.length > 0) {
      setShowDateFilter(true);
      return;
    }

    if (!orgId) return;
    setLoadingMetadata(true);
    try {
      const metadata = await fetchSourceMetadata(orgId, card.dataSource.id);
      const dateCols = metadata.columns.filter(isDateColumn);
      setDateColumns(metadata.columns);
      if (dateCols.length === 0) {
        setNoDateColumns(true);
      } else {
        setShowDateFilter(true);
        // Auto-select first date column if none set
        if (!card.dateColumn) {
          onDateFilterChange?.(
            card.id,
            dateCols[0].name,
            dateCols[0].display_name || dateCols[0].name,
            "this-year",
            undefined,
          );
        }
      }
    } catch {
      // Silently fail — user can still import without filter
    } finally {
      setLoadingMetadata(false);
    }
  };

  const handleOpenEditFilter = async () => {
    // Reset edit state to current card values
    setEditDateColumn(card.dateColumn);
    setEditDatePreset(card.datePreset);
    setEditAfterMonth(card.afterMonth);

    // Fetch metadata if not already loaded
    if (dateColumns.length === 0 && orgId) {
      setLoadingMetadata(true);
      try {
        const metadata = await fetchSourceMetadata(orgId, card.dataSource.id);
        setDateColumns(metadata.columns);
        const dateCols = metadata.columns.filter(isDateColumn);
        if (dateCols.length === 0) {
          setNoDateColumns(true);
          return;
        }
        // Auto-select first date column if none set
        if (!card.dateColumn) {
          setEditDateColumn(dateCols[0].name);
          setEditDatePreset("this-year");
        }
      } catch {
        return;
      } finally {
        setLoadingMetadata(false);
      }
    }
    setIsEditingFilter(true);
  };

  const handleSaveAndRefresh = () => {
    setIsEditingFilter(false);
    onDateFilterChangeAndRefresh?.(card.id, editDateColumn, getColumnDisplayName(editDateColumn), editDatePreset, editAfterMonth);
  };

  const handleCancelEdit = () => {
    setIsEditingFilter(false);
    // Revert to original values
    setEditDateColumn(card.dateColumn);
    setEditDatePreset(card.datePreset);
    setEditAfterMonth(card.afterMonth);
  };

  const getColumnDisplayName = (colName: string | undefined): string | undefined => {
    if (!colName) return undefined;
    const col = dateColumns.find((c) => c.name === colName);
    return col?.display_name || col?.name || colName;
  };

  const handleRemoveDateFilter = () => {
    setShowDateFilter(false);
    setNoDateColumns(false);
    onDateFilterChange?.(card.id, undefined, undefined, undefined, undefined);
  };

  const borderClass =
    card.status === "error"
      ? "border border-spark/30"
      : card.status === "warning"
        ? "border border-[#D97706]/30"
        : "border border-bg3";

  return (
    <div className={`group/card relative rounded-lg p-4 bg-bg-elevated transition-colors duration-200 ${borderClass}`}>
      {/* Dots menu — overlaid top right, visible on hover */}
      {card.status === "imported" && (
        <div className="absolute top-3 right-3 opacity-0 group-hover/card:opacity-100 transition-opacity z-10">
          <Menu as="div" className="relative">
            <Menu.Button
              as={Button}
              variant="tertiary"
              size="xs"
              className="bg-bg-elevated"
              leadingIcon={<MoreVertical size={14} />}
              aria-label={`Actions for ${card.dataSource.name}`}
            />
              <Menu.Items className="absolute right-0 mt-1 w-44 bg-bg-elevated rounded-lg shadow-lg border border-bg3 p-1 z-50 focus:outline-none">
                <Menu.Item>
                  {({ active }) => (
                    <button
                      onClick={() => onRefresh?.(card.id)}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-[13px] text-fg rounded-md transition-colors ${active ? "bg-bg2" : ""}`}
                    >
                      <RefreshCw size={14} className="text-fg2" />
                      <span>Refresh</span>
                    </button>
                  )}
                </Menu.Item>
                <Menu.Item>
                  {({ active }) => (
                    <button
                      onClick={() => setShowConfirm(true)}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-[13px] text-fg rounded-md transition-colors ${active ? "bg-bg2" : ""}`}
                    >
                      <Unlink size={14} className="text-fg2" />
                      <span>Disconnect</span>
                    </button>
                  )}
                </Menu.Item>
              </Menu.Items>
            </Menu>
          </div>
        )}

      {/* Title row */}
      <div className="flex gap-2 items-start justify-start">
        <span className="text-fg font-display-heading text-[14px] min-w-0 flex-1 font-[500]">
          {card.dataSource.name}
        </span>

        {/* Action buttons — top right (non-imported states) */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {card.status === "pending" && (
            <Button
              variant="tertiary"
              size="xs"
              leadingIcon={<X size={14} />}
              onClick={() => onRemove?.(card.id)}
              aria-label={`Remove ${card.dataSource.name}`}
            />
          )}

          {card.status === "error" && (
            <Button
              variant="tertiary"
              size="xs"
              leadingIcon={<X size={14} />}
              onClick={() => onRemove?.(card.id)}
              aria-label={`Remove ${card.dataSource.name}`}
            />
          )}

          {card.status === "warning" && (
            <Button
              variant="tertiary"
              size="xs"
              leadingIcon={<X size={14} />}
              onClick={() => onCancelWarning?.(card.id)}
              aria-label="Dismiss warning"
            />
          )}
        </div>
      </div>

      {/* Pending: date filter + Import button */}
      {card.status === "pending" && (
        <div className="mt-3 flex flex-col gap-3">
          {!showDateFilter ? (
            <Button
              variant="secondary"
              size="sm"
              leadingIcon={<Plus size={13} />}
              label={loadingMetadata ? "Loading…" : noDateColumns ? "No date columns" : "Add date filter"}
              onClick={handleOpenDateFilter}
              disabled={loadingMetadata || noDateColumns}
            />
          ) : (
            <DateRangeConfig
              columns={dateColumns}
              dateColumn={card.dateColumn || null}
              datePreset={card.datePreset || null}
              afterMonth={card.afterMonth || null}
              onDateColumnChange={(col) =>
                onDateFilterChange?.(card.id, col, getColumnDisplayName(col), card.datePreset, card.afterMonth)
              }
              onDatePresetChange={(preset) =>
                onDateFilterChange?.(card.id, card.dateColumn, card.dateColumnDisplayName, preset, card.afterMonth)
              }
              onAfterMonthChange={(month) =>
                onDateFilterChange?.(card.id, card.dateColumn, card.dateColumnDisplayName, card.datePreset, month)
              }
              onRemove={handleRemoveDateFilter}
            />
          )}
          <Button
            variant="primary"
            size="sm"
            leadingIcon={<Download size={13} />}
            label="Import data"
            onClick={() => onImport?.(card.id)}
          />
        </div>
      )}

      {/* Loading: shimmer status text */}
      {card.status === "loading" && (
        <div className="mt-3">
          <ShimmerText
            text={card.loadingMessage || `Fetching ${card.dataSource.name}…`}
            className="text-[12px] font-normal"
          />
        </div>
      )}

      {/* Imported: date filter display, sheet name + timestamp */}
      {card.status === "imported" && !isEditingFilter && (
        <div className="mt-3 space-y-[12px] animate-fadeIn">
          {card.lastRefreshed && (
            <div className="flex flex-col items-start">
              {/* Date filter button */}
              <Button
                variant="secondary"
                size="xs"
                leadingIcon={<Calendar size={12} />}
                onClick={handleOpenEditFilter}
                disabled={loadingMetadata}
                className="max-w-full min-w-0"
                aria-label={
                  card.dateColumn && card.datePreset
                    ? `Edit date filter for ${card.dataSource.name}`
                    : `Add date filter for ${card.dataSource.name}`
                }
              >
                <span className="truncate">
                  {loadingMetadata ? "Loading…" : dateFilterLabel}
                </span>
              </Button>
              {/* Sheet name + timestamp */}
              <div className="mt-3 space-y-1">
                {card.sheetName && (
                  <span className="text-[12px] text-fg2 leading-snug block">
                    Connected to{" "}
                    <button
                      className="text-fg2 underline decoration-fg3 hover:text-fg transition-colors cursor-pointer bg-transparent border-none p-0 text-[12px]"
                      onClick={() => card.sheetId && onSheetClick?.(card.sheetId)}
                      aria-label={`Go to sheet ${card.sheetName}`}
                    >
                      {card.sheetName}
                    </button>
                  </span>
                )}
                <span
                  className={`text-[12px] leading-snug font-normal block ${
                    card.isStale ? "text-[#D97706] font-medium" : "text-fg2"
                  }`}
                >
                  {formatTimestamp(card.lastRefreshed)}
                </span>
              </div>
            </div>
          )}
          {card.truncated && (
            <Alert
              variant="info"
              size="small"
              title="Some rows were not imported because the dataset exceeds Excel's row limit. Consider narrowing the date range."
            />
          )}
        </div>
      )}

      {/* Imported: editing date filter inline */}
      {card.status === "imported" && isEditingFilter && (
        <div className="mt-3 flex flex-col gap-3">
          <DateRangeConfig
            columns={dateColumns}
            dateColumn={editDateColumn || null}
            datePreset={editDatePreset || null}
            afterMonth={editAfterMonth || null}
            onDateColumnChange={(col) => setEditDateColumn(col)}
            onDatePresetChange={(preset) => setEditDatePreset(preset)}
            onAfterMonthChange={(month) => setEditAfterMonth(month)}
            onRemove={handleCancelEdit}
          />
          <div className="flex items-center gap-2">
            <Button
              variant="primary"
              size="xs"
              leadingIcon={<RefreshCw size={12} />}
              label="Save & Refresh"
              onClick={handleSaveAndRefresh}
            />
            <Button
              variant="secondary"
              size="xs"
              label="Clear Filter & Refresh"
              onClick={() => {
                setIsEditingFilter(false);
                onDateFilterChangeAndRefresh?.(card.id, undefined, undefined, undefined, undefined);
              }}
            />
          </div>
        </div>
      )}

      {/* Warning: with proceed/cancel */}
      {card.status === "warning" && (
        <div className="mt-3 space-y-3">
          <Alert
            variant="warning"
            size="small"
            title={
              card.warningMessage ||
              `This dataset contains ${card.rowCount?.toLocaleString()} rows, which may be slow to import. Consider narrowing the date range.`
            }
          />
          <div className="flex items-center gap-2">
            <Button
              variant="primary"
              size="xs"
              label={card.refreshWarningType ? "Refresh Anyway" : "Proceed Anyway"}
              onClick={() => onProceedWithWarning?.(card.id)}
            />
            <Button
              variant="tertiary"
              size="xs"
              label="Cancel"
              onClick={() => onCancelWarning?.(card.id)}
            />
          </div>
        </div>
      )}

      {/* Error: message + retry or reimport */}
      {card.status === "error" && (
        <div className="mt-3 space-y-3">
          <Alert variant="error" size="small" title={card.errorMessage} />
          <div className="flex items-center gap-2">
            {card.sheetDeleted ? (
              <Button
                variant="primary"
                size="xs"
                label="Import in a new sheet"
                leadingIcon={<Download size={12} />}
                onClick={() => onReimport?.(card.id)}
              />
            ) : (
              <Button
                variant="secondary"
                size="xs"
                label="Retry"
                leadingIcon={<RotateCcw size={12} />}
                onClick={() => onRetry?.(card.id)}
              />
            )}
          </div>
        </div>
      )}

      {/* Inline remove confirmation for imported cards */}
      {showConfirm && card.status === "imported" && (
        <div className="mt-3 p-3 bg-bg2 rounded-md">
          <p className="text-[13px] text-fg2 leading-relaxed mb-2">
            If you disconnect this data source, the data you&apos;ve imported in the sheet &ldquo;{card.sheetName}&rdquo; will stay there &mdash; it just won&apos;t refresh automatically anymore.
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="primary"
              size="xs"
              label="Disconnect"
              onClick={() => {
                onRemove?.(card.id);
                setShowConfirm(false);
              }}
            />
            <Button
              variant="tertiary"
              size="xs"

              label="Cancel"
              onClick={() => setShowConfirm(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
}
